/**
 * Native Google Play Billing helper.
 *
 * On native Android we MUST use Google Play Billing for digital goods —
 * Stripe is forbidden by Play Store policy and would get the app removed.
 * On web we fall back to the existing Stripe checkout.
 *
 * Plugin: @capgo/native-purchases (Google Play Billing 7). Unlike the old
 * @squareetlabs/capacitor-subscriptions plugin — which hardcoded
 * ProductType.SUBS and therefore could NOT purchase one-time products —
 * this plugin supports BOTH one-time (INAPP) and subscription (SUBS)
 * product types. Both Fitjong premium items (remove_ads, pro_fitness_pack)
 * are configured as ONE-TIME products in Play Console, so we always pass
 * PURCHASE_TYPE.INAPP.
 *
 * Flow:
 *   1. purchaseProduct() opens the native Play purchase dialog and resolves
 *      with a Transaction object that contains `.purchaseToken` directly
 *      (no listener needed). It auto-acknowledges the purchase by default
 *      so Google won't refund it after 3 days.
 *   2. We POST that purchaseToken + product_id to /api/iap/verify-android —
 *      the backend verifies the token with Google's Developer API
 *      (purchases.products.get) and grants the items server-side.
 *
 * Product ID convention: Google Play in-app product IDs are exactly as
 * entered in Play Console — NOT package-prefixed. Our SKUs map 1:1 to the
 * Play Console product IDs:
 *   remove_ads        → "remove_ads"
 *   pro_fitness_pack  → "pro_fitness_pack"
 * The same bare product ID is sent to the backend, which Google's
 * purchases.products.get verification also expects (packageName is passed
 * separately).
 */
import { Capacitor } from "@capacitor/core";
import { NativePurchases, PURCHASE_TYPE } from "@capgo/native-purchases";
import { api } from "./api";

// Cached after first probe so render paths can answer synchronously.
let _pluginAvailable = null;

/**
 * True only if we're on Android native AND Google Play Billing is actually
 * available on this device. Falls back to false on web / when billing is
 * unsupported so the store can show a graceful message.
 */
export async function isAndroidNativeWithBilling() {
  if (!Capacitor.isNativePlatform()) return false;
  if (Capacitor.getPlatform() !== "android") return false;
  if (_pluginAvailable !== null) return _pluginAvailable;
  try {
    const res = await NativePurchases.isBillingSupported();
    _pluginAvailable = !!res?.isBillingSupported;
  } catch (e) {
    const msg = String(e?.message || e);
    // Plugin not compiled into the build → treat as unavailable.
    _pluginAvailable = /not implemented/i.test(msg) ? false : false;
  }
  return _pluginAvailable;
}

// Synchronous variant for render paths that don't want to await.
// Optimistically returns true on Android; the async path downgrades to
// false on first failure and subsequent calls return the cached value.
export function isAndroidNative() {
  if (!Capacitor.isNativePlatform()) return false;
  if (Capacitor.getPlatform() !== "android") return false;
  return _pluginAvailable !== false; // null or true ⇒ act native
}

export function skuToProductId(sku) {
  // Google Play product IDs are NOT package-prefixed — they equal the SKU
  // exactly as configured in Play Console (e.g. "remove_ads").
  return sku;
}

// Coin packs are CONSUMABLE (you can re-buy them) — they must be consumed
// after granting or Google keeps them in an "owned" state and blocks repeat
// purchases. Premium items (remove_ads, pro_fitness_pack) are one-time
// non-consumable entitlements, so they are acknowledged, not consumed.
const CONSUMABLE_SKUS = new Set(["coins_small", "coins_medium", "coins_large"]);

/**
 * Buy a premium / coin-pack SKU on native Android. Throws Error with a
 * user-readable message if the plugin is missing, the user cancels, or
 * verification fails.
 *
 * All Fitjong real-money products are one-time (INAPP). Coin packs are
 * CONSUMABLE so they can be re-bought (isConsumable=true → Google consumes
 * the token after the entitlement is granted); premium items are
 * non-consumable and are auto-acknowledged. After the Play purchase
 * succeeds we POST the token to /api/iap/verify-android — and because the
 * user has ALREADY paid by that point, we retry the verify call a couple of
 * times so a transient network blip doesn't leave them charged with nothing.
 */
export async function buyOnAndroid(sku) {
  const ok = await isAndroidNativeWithBilling();
  if (!ok) {
    throw new Error(
      "In-app purchases will be available in the next update. Please try again soon."
    );
  }
  const productId = skuToProductId(sku);
  const isConsumable = CONSUMABLE_SKUS.has(sku);

  let transaction;
  try {
    transaction = await NativePurchases.purchaseProduct({
      productIdentifier: productId,
      productType: PURCHASE_TYPE.INAPP,
      isConsumable,
    });
  } catch (err) {
    const msg = String(err?.message || err);
    if (/not implemented/i.test(msg)) {
      _pluginAvailable = false;
      throw new Error("In-app purchases are not yet available in this build.");
    }
    if (/cancel/i.test(msg)) {
      throw new Error("Purchase was cancelled.");
    }
    throw new Error(
      msg ||
      "Couldn't open the Google Play purchase screen. " +
      "Make sure you're signed in to Google Play and try again."
    );
  }

  const purchaseToken = transaction?.purchaseToken;
  if (!purchaseToken) {
    throw new Error("No purchase token returned from Google Play.");
  }

  // The money has already left the user's account, so be resilient: retry the
  // server verification on transient network/5xx errors before giving up.
  const payload = { purchase_token: purchaseToken, product_id: productId, sku };
  let data, lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await api.post("/iap/verify-android", payload, { timeout: 35000 });
      data = res.data;
      break;
    } catch (e) {
      lastErr = e;
      // Don't retry a definitive client rejection (4xx) — only network/5xx.
      const status = e?.response?.status;
      if (status && status >= 400 && status < 500) break;
      await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
    }
  }
  if (!data) {
    const detail = lastErr?.response?.data?.detail;
    throw new Error(
      detail ||
      "Your payment went through but we couldn't confirm it just now. " +
      "It will be applied automatically — please reopen the Store in a moment."
    );
  }

  if (!data?.verified) {
    throw new Error("Backend could not verify the purchase.");
  }
  return data;
}
